import React from 'react';
import { useSelector } from 'react-redux';
import { sendGALinkClickEvent } from '@noths/polaris-client-google-analytics';
import { IconChevronRight } from '@noths/polaris-client-ribbons-design-system';

import { DeliveryEventMessage } from 'src/components/atoms/DeliveryEventMessage/DeliveryEventMessage';
import { DELIVERY_DRAWER_URL_FRAGMENT_ID } from 'src/fragmentIDs';
import { selectDeliveryZones, selectSelectedZoneCode } from 'src/redux/delivery/selectors';
import type { ReduxApplicationState } from 'src/redux/types';
import { deliveryOptionArrivesOnOrByDate } from '../DeliveryOptions/utils/deliveryOptionArrivesOnOrByDate';
import { getCutoffMessage } from '../DeliveryOptions/utils/getCutoffMessage';
import { getDeliveryOptionText } from '../DeliveryOptions/utils/getDeliveryOptionText';
import { getFormattedDeliveryDate } from '../DeliveryOptions/utils/getFormattedDeliveryDate';
import { getFormattedPrice } from '../DeliveryOptions/utils/getFormattedPrice';
import { PackageIcon } from './PackageIcon';
import * as styles from './DeliverySummary.styles';

export const DeliverySummary = () => {
  const selectedDeliveryZone = useSelector((state: ReduxApplicationState) => {
    const allDeliveryZones = selectDeliveryZones(state);
    const selectedZoneCode = selectSelectedZoneCode(state);

    return (allDeliveryZones || []).find((zone) => {
      return zone.zoneCode === selectedZoneCode;
    });
  });
  const deliveryOptions = selectedDeliveryZone?.deliveryOptions || [];
  const deliveryEvent = useSelector((state: ReduxApplicationState) => {
    return state.delivery.deliveryEvent;
  });

  const onDrawerLinkClick = (ev: React.MouseEvent<HTMLAnchorElement>) => {
    sendGALinkClickEvent(ev, {
      event: 'custom_event_v2',
      event_name: 'select_content',
      event_details: 'link click',
      content_type: 'delivery information',
      item_id: 'delivery and returns details',
    });
  };

  const specialSize = selectedDeliveryZone?.exceptional || false;
  const hasOptionThatArrivesByEvent = deliveryEvent
    ? deliveryOptions.some((option) => deliveryOptionArrivesOnOrByDate(option, deliveryEvent.date))
    : false;

  const [option1, option2] = deliveryOptions;
  const option1CutoffMessage = option1 ? getCutoffMessage(option1.cutoffDateTime) : null;

  return (
    <>
      {deliveryEvent && hasOptionThatArrivesByEvent && (
        <DeliveryEventMessage eventName={deliveryEvent.name} />
      )}
      <a
        css={styles.deliverySummary}
        href={`#${DELIVERY_DRAWER_URL_FRAGMENT_ID}`}
        onClick={onDrawerLinkClick}
      >
        <PackageIcon />
        <ul>
          {deliveryOptions.length === 0 ? (
            <li>Delivery &amp; returns info</li>
          ) : (
            <>
              {option1 && (
                <li>
                  {option1CutoffMessage && (
                    <small>
                      <b>Order by {option1CutoffMessage}</b>
                    </small>
                  )}
                  <br />
                  {specialSize
                    ? 'Special size delivery '
                    : option1.optionCode === 'SATURDAY'
                    ? 'Get it on '
                    : 'Estimated delivery: '}
                  <b>
                    {option1.exactDate && option1.date ? (
                      <time dateTime={option1.date}>{getFormattedDeliveryDate(option1.date)}</time>
                    ) : (
                      `${option1.min}-${option1.max} days`
                    )}{' '}
                    ({getFormattedPrice(Number(option1.price.amount), option1.price.currencyCode)})
                  </b>
                </li>
              )}
              {option2 && (
                <li>
                  <small>
                    Or{' '}
                    {option2.exactDate && option2.date ? (
                      <time dateTime={option2.date}>{getFormattedDeliveryDate(option2.date)}</time>
                    ) : (
                      `${option2.min}-${option2.max} days`
                    )}{' '}
                    ({getFormattedPrice(Number(option2.price.amount), option2.price.currencyCode)}){' '}
                    with {getDeliveryOptionText(option2.optionCode)}
                  </small>
                </li>
              )}
            </>
          )}
        </ul>
        <IconChevronRight color="var(--neutral-black-base)" height={16} width={16} />
      </a>
    </>
  );
};
